import produce from "immer"
import { Point } from "leaflet"
import type { Bounds, LatLng, LatLngExpression, Map as LeafletMap } from "leaflet"
import { useEffect, useMemo, useRef, useState } from "react"
import type { ReactElement, RefObject } from "react"
import { Polyline, useMap } from "react-leaflet"
import { ShapePopupPanel } from ".."
import { useCallbackSafeState } from "../../../hooks"
import type { IMapDrawing, IPolyline, IShape } from "../../../redux"
import { getPolylinePixelBounds } from "../../../utils"
import type { RectangleOverwriteLayer } from "../../../utils"
import { RectangleShapeOverwrite } from "../../atoms"

export interface IMapPolylineShapeProps {
  drawing: IMapDrawing
}

export function MapPolylineShape(props: IMapPolylineShapeProps): ReactElement {
  const { drawing } = props

  const [polyLineLayer, setPolyLineLayer] = useState<L.Polyline>()
  const [rectangleLayer, setRectangleLayer] = useState<RectangleOverwriteLayer>()

  const polyRef: RefObject<L.Polyline | undefined> = useRef<L.Polyline>()
  const rectRef: RefObject<RectangleOverwriteLayer | undefined> = useRef<RectangleOverwriteLayer>()

  const [getShape, setShape] = useCallbackSafeState<IPolyline>(drawing.shape as IPolyline)
  const map: LeafletMap = useMap()
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    setShape(drawing.shape as IPolyline)
  }, [drawing.shape])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    if (polyRef.current) {
      setPolyLineLayer(polyRef.current)
    }
  }, [polyRef])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    if (rectRef.current) {
      setRectangleLayer(rectRef.current)
    }
  }, [rectRef])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  const corner1: LatLng = useMemo<LatLng>(() => {
    const points: Point[] = getShape().positions.map((latlng: LatLng) => {
      return map.latLngToLayerPoint(latlng)
    })
    const bounds: Bounds = getPolylinePixelBounds(points)
    return map.layerPointToLatLng(bounds.getTopLeft())
  }, [getShape().positions])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  const corner2: LatLng = useMemo<LatLng>(() => {
    const points: Point[] = getShape().positions.map((latlng: LatLng) => {
      return map.latLngToLayerPoint(latlng)
    })
    const bounds: Bounds = getPolylinePixelBounds(points)
    return map.layerPointToLatLng(bounds.getBottomRight())
  }, [getShape().positions])

  function duplicateShape(offsetFunction: (coord: LatLng) => LatLng): IShape {
    return produce(getShape(), (draft: IPolyline) => {
      draft.positions = draft.positions.map((position: LatLng) => {
        return offsetFunction(position)
      })
    })
  }

  function calculatePopupLocation(): LatLngExpression {
    const centerPoint: Point = (rectangleLayer as any).editing._getCenterPoint() as Point
    const corner1: Point = map.latLngToLayerPoint((rectangleLayer as RectangleOverwriteLayer)._corner1)

    const point: Point = new Point(centerPoint.x, corner1.y)
    const coord: LatLng = map.layerPointToLatLng(point)
    return [coord.lat, coord.lng]
  }

  return (
    <>
      <Polyline
        ref={polyRef as RefObject<L.Polyline>}
        fillOpacity={0}
        positions={getShape().positions}
        color={getShape().color}
        weight={getShape().lineWidth}
      />
      <RectangleShapeOverwrite
        ref={rectRef as RefObject<RectangleOverwriteLayer>}
        pathOptions={{
          opacity: 0,
          fillOpacity: 0
        }}
        corner1={corner1}
        corner2={corner2}
      >
        {rectangleLayer && polyLineLayer && (
          <ShapePopupPanel
            shapeLayer={polyLineLayer}
            proxyLayer={rectangleLayer}
            drawing={drawing}
            createDuplicate={duplicateShape}
            calculatePopupLocation={calculatePopupLocation}
          />
        )}
      </RectangleShapeOverwrite>
    </>
  )
}
